/**
 * Authentication state management
 * Enhanced with robust error handling, token refresh, and real-time sync
 * Supports Google Sign-In and Phone Number Sign-In
 */

import { create } from 'zustand';
import {
  User,
  onAuthStateChanged,
  onIdTokenChanged,
  getRedirectResult,
  ConfirmationResult,
  signInWithCredential,
} from 'firebase/auth';
import { doc, getDoc, setDoc, Timestamp, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import {
  auth,
  authReady,
  db,
  clearRecaptchaVerifier,
  getAuthErrorMessage,
  isNetworkError,
} from '../lib/firebase';
import { normalizeUserDoc } from '../lib/firestoreHelpers';
import {
  signInWithGoogle as platformSignInWithGoogle,
  signInWithApple as platformSignInWithApple,
  signInWithPhone as platformSignInWithPhone,
  signOut as platformSignOut,
  confirmPhoneWithCode as platformConfirmPhoneWithCode,
  isAppleSignInAvailable,
} from '../lib/auth/platformAuth';
import type { UserDocument, TimeOption } from '../lib/types';

interface AuthState {
  user: User | null;
  userDoc: UserDocument | null;
  loading: boolean;
  initialized: boolean;
  /** True while userDoc is being fetched for the first time after auth */
  userDocLoading: boolean;
  error: string | null;
  phoneVerificationId: string | null;
  confirmationResult: ConfirmationResult | null;
  isOnline: boolean;

  // Derived state helpers
  /** True when both auth and userDoc are ready for routing decisions */
  isBootstrapReady: () => boolean;

  // Actions
  initialize: () => () => void;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  isAppleSignInAvailable: () => boolean;
  sendPhoneCode: (phoneNumber: string) => Promise<void>;
  verifyPhoneCode: (code: string) => Promise<void>;
  resendPhoneCode: () => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  fetchUserDoc: () => Promise<void>;
  updateUserSettings: (updates: Partial<UserDocument>) => Promise<void>;
  createUserDocIfNeeded: (user: User, displayName?: string) => Promise<void>;
  clearError: () => void;
  refreshToken: () => Promise<string | null>;
  setupUserDocListener: (uid: string) => void;
}

// Track the user document listener for cleanup
let userDocUnsubscribe: Unsubscribe | null = null;
// Track the last phone number for resend functionality
let lastPhoneNumber: string | null = null;
// Track if auth has been initialized to prevent double initialization
let authInitialized = false;
let authUnsubscribe: (() => void) | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  userDoc: null,
  loading: true,
  initialized: false,
  userDocLoading: false,
  error: null,
  phoneVerificationId: null,
  confirmationResult: null,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,

  /**
   * Returns true when bootstrap is complete and routing decisions can be made.
   * - If no user: ready (will route to login)
   * - If user exists: ready only after userDoc has been fetched (even if null for new users)
   */
  isBootstrapReady: () => {
    const { initialized, user, userDocLoading } = get();
    if (!initialized) return false;
    if (!user) return true; // No user = ready to route to login
    return !userDocLoading; // User exists = wait for userDoc fetch to complete
  },

  /**
   * Initialize auth state listener with proper cleanup
   * Sets up both auth state and token refresh listeners
   * Idempotent - safe to call multiple times (important for React StrictMode)
   *
   * CRITICAL: Waits for Firebase authReady before marking initialized to prevent
   * route guards from redirecting to /auth before session is restored from persistence.
   */
  initialize: () => {
    // Guard against double initialization (React StrictMode calls effects twice)
    if (authInitialized && authUnsubscribe) {
      return authUnsubscribe;
    }

    authInitialized = true;

    // Track online/offline status
    const handleOnline = () => set({ isOnline: true });
    const handleOffline = () => set({ isOnline: false, error: 'You appear to be offline.' });

    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
    }

    // Check for redirect result (for popup fallback scenarios)
    getRedirectResult(auth).then((result) => {
      if (result?.user) {
        get().createUserDocIfNeeded(result.user, result.user.displayName || undefined);
      }
    }).catch(() => {
      // Silently ignore - redirect result errors are expected when no redirect occurred
    });

    // Wait for auth to be ready (session restored from persistence) before setting initialized
    // This prevents the route guard from redirecting to /auth before the session is rehydrated
    authReady.then(() => {
      // Main auth state listener - now safe to set initialized
      const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
        // Cleanup previous user doc listener
        if (userDocUnsubscribe) {
          userDocUnsubscribe();
          userDocUnsubscribe = null;
        }

        if (user) {
          // User exists - mark userDocLoading true until we get the first snapshot
          // Clear any previous auth errors since sign-in succeeded
          set({ user, loading: false, initialized: true, userDocLoading: true, error: null });
          // Ensure user document exists
          await get().createUserDocIfNeeded(user);
          // Set up real-time listener for user document (will set userDocLoading: false on first snapshot)
          get().setupUserDocListener(user.uid);
        } else {
          // No user - no userDoc to load
          set({ user: null, userDoc: null, loading: false, initialized: true, userDocLoading: false });
          lastPhoneNumber = null;
        }
      });

      // Token refresh listener - keeps token fresh
      const unsubscribeToken = onIdTokenChanged(auth, async (user) => {
        if (user) {
          // Token was refreshed, update user reference
          set({ user });
        }
      });

      // Handle app resume (visibility change) to validate/refresh session
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          const { user } = get();
          if (user) {
            // Silently refresh token on resume to ensure session is still valid
            user.getIdToken(false).catch(() => {
              // Token refresh failed - session may have expired
              // onAuthStateChanged will handle this if the user is truly logged out
            });
          }
        }
      };

      if (typeof window !== 'undefined') {
        document.addEventListener('visibilitychange', handleVisibilityChange);
      }

      // Update stored cleanup function with all listeners
      const previousCleanup = authUnsubscribe;
      authUnsubscribe = () => {
        unsubscribeAuth();
        unsubscribeToken();
        if (userDocUnsubscribe) {
          userDocUnsubscribe();
          userDocUnsubscribe = null;
        }
        if (typeof window !== 'undefined') {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
        clearRecaptchaVerifier();
        authInitialized = false;
        authUnsubscribe = null;
      };

      // If a previous cleanup was stored before authReady, it won't have the listeners
      // This is handled by the guard at the top of initialize()
      void previousCleanup;
    });

    // Initial cleanup function (before authReady resolves)
    // This will be replaced once authReady resolves
    authUnsubscribe = () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      }
      clearRecaptchaVerifier();
      authInitialized = false;
      authUnsubscribe = null;
    };

    return authUnsubscribe;
  },

  /**
   * Set up real-time listener for user document
   * Sets userDocLoading: false on first snapshot (success or doc not found)
   */
  setupUserDocListener: (uid: string) => {
    const userDocRef = doc(db, 'users', uid);

    userDocUnsubscribe = onSnapshot(
      userDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const normalizedDoc = normalizeUserDoc(data);

          // Check if de-escalation mode has expired
          if (normalizedDoc.mode === 'deescalation' && normalizedDoc.modeUntil) {
            if (normalizedDoc.modeUntil.getTime() < Date.now()) {
              // Mode expired - reset to normal (server will also enforce this)
              normalizedDoc.mode = 'normal';
              normalizedDoc.modeUntil = undefined;
            }
          }

          set({ userDoc: normalizedDoc, userDocLoading: false });
        } else {
          // Document doesn't exist yet (new user) - still mark loading complete
          set({ userDoc: null, userDocLoading: false });
        }
      },
      () => {
        // Listener error - mark loading complete and fall back to one-time fetch
        set({ userDocLoading: false });
        get().fetchUserDoc();
      }
    );
  },

  /**
   * Sign in with Google - always tries popup first, falls back to redirect if blocked
   * Popup is more reliable and doesn't lose auth state like redirect can
   * In native app, uses native auth flow
   */
  signInWithGoogle: async () => {
    const { isOnline } = get();

    if (!isOnline) {
      set({ error: 'You appear to be offline. Please check your connection.' });
      return;
    }

    set({ loading: true, error: null });

    try {
      await platformSignInWithGoogle();
      set({ loading: false });
    } catch (error) {
      const message = getAuthErrorMessage(error);
      set({ error: message, loading: false });
      throw error;
    }
  },

  /**
   * Sign in with Apple - iOS native only
   * Required for App Store compliance (Guideline 4.8) when offering Google Sign-In
   */
  signInWithApple: async () => {
    const { isOnline } = get();

    if (!isOnline) {
      set({ error: 'You appear to be offline. Please check your connection.' });
      return;
    }

    if (!isAppleSignInAvailable()) {
      set({ error: 'Sign in with Apple is only available on iOS.' });
      return;
    }

    set({ loading: true, error: null });

    try {
      await platformSignInWithApple();
      set({ loading: false });
    } catch (error) {
      const message = getAuthErrorMessage(error);
      set({ error: message, loading: false });
      throw error;
    }
  },

  /**
   * Check if Sign in with Apple is available (iOS native only)
   */
  isAppleSignInAvailable: () => isAppleSignInAvailable(),

  /**
   * Send phone verification code
   * Native: plugin sends SMS and returns verificationId. Web: reCAPTCHA + send code.
   */
  sendPhoneCode: async (phoneNumber: string) => {
    const { isOnline } = get();

    if (!isOnline) {
      set({ error: 'You appear to be offline. Please check your connection.' });
      return;
    }

    set({ loading: true, error: null });
    lastPhoneNumber = phoneNumber;

    try {
      const result = await platformSignInWithPhone(phoneNumber);

      if ('verificationId' in result) {
        set({
          confirmationResult: null,
          phoneVerificationId: result.verificationId,
          loading: false,
        });
      } else {
        set({
          confirmationResult: result.confirmationResult,
          phoneVerificationId: result.confirmationResult.verificationId,
          loading: false,
        });
      }
    } catch (error) {
      clearRecaptchaVerifier();

      // User-friendly error message
      const message = getAuthErrorMessage(error);
      set({ error: message, loading: false });

      throw error;
    }
  },

  /**
   * Resend phone verification code with fresh reCAPTCHA
   */
  resendPhoneCode: async () => {
    if (!lastPhoneNumber) {
      set({ error: 'No phone number to resend to. Please start over.' });
      return;
    }

    // Clear previous verification state and force fresh reCAPTCHA
    set({ confirmationResult: null, phoneVerificationId: null });
    clearRecaptchaVerifier();

    // Resend to the same number
    await get().sendPhoneCode(lastPhoneNumber);
  },

  /**
   * Verify phone code with enhanced error handling
   */
  verifyPhoneCode: async (code: string) => {
    const { confirmationResult, phoneVerificationId, isOnline } = get();

    if (!isOnline) {
      set({ error: 'You appear to be offline. Please check your connection.' });
      return;
    }

    set({ loading: true, error: null });

    try {
      if (confirmationResult) {
        await confirmationResult.confirm(code);
      } else if (phoneVerificationId) {
        const credential = await platformConfirmPhoneWithCode(phoneVerificationId, code);
        await signInWithCredential(auth, credential);
      } else {
        throw new Error('No verification in progress. Please request a new code.');
      }

      // Clear verification state and reCAPTCHA
      set({ confirmationResult: null, phoneVerificationId: null });
      clearRecaptchaVerifier();
      lastPhoneNumber = null;
      // onAuthStateChanged will handle the rest
    } catch (error) {
      const message = getAuthErrorMessage(error);
      set({ error: message, loading: false });
      throw error;
    }
  },

  /**
   * Check if user document exists (no longer creates it - that happens after onboarding)
   * This is kept for compatibility but doesn't create the doc anymore
   */
  createUserDocIfNeeded: async (_user: User, _displayName?: string) => {
    // No-op: User document is now created only after onboarding completion
    // This prevents partial user records from being created before user completes setup
    void _user;
    void _displayName;
  },

  /**
   * Sign out with proper cleanup
   */
  signOut: async () => {
    set({ loading: true, error: null });

    try {
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
        userDocUnsubscribe = null;
      }
      clearRecaptchaVerifier();
      lastPhoneNumber = null;

      await platformSignOut();

      set({
        user: null,
        userDoc: null,
        loading: false,
        confirmationResult: null,
        phoneVerificationId: null,
        error: null,
      });
    } catch (error) {
      const message = getAuthErrorMessage(error);
      set({ error: message, loading: false });
    }
  },

  /**
   * Delete user account - required for App Store compliance (Guideline 5.1.1)
   * Deletes user's Firestore document and Firebase Auth account
   */
  deleteAccount: async () => {
    const { user, isOnline } = get();

    if (!isOnline) {
      set({ error: 'You appear to be offline. Please check your connection.' });
      throw new Error('Offline');
    }

    if (!user) {
      set({ error: 'Not signed in.' });
      throw new Error('Not signed in');
    }

    set({ loading: true, error: null });

    try {
      // Cleanup listeners before deletion
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
        userDocUnsubscribe = null;
      }
      clearRecaptchaVerifier();
      lastPhoneNumber = null;

      // Call the comprehensive deletion callable function
      // This handles all Firestore data, Storage files, household cleanup, and Auth deletion
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const deleteAccountData = httpsCallable<void, { success: boolean; phase: string; message: string }>(
        functions,
        'deleteAccountData'
      );

      const result = await deleteAccountData();

      if (!result.data.success) {
        throw new Error(result.data.message || 'Account deletion failed');
      }

      // Clear local state - auth user already deleted by Cloud Function
      set({
        user: null,
        userDoc: null,
        loading: false,
        confirmationResult: null,
        phoneVerificationId: null,
        error: null,
      });
    } catch (error) {
      // Check for requires-recent-login error
      const firebaseError = error as { code?: string; message?: string };
      if (firebaseError.code === 'auth/requires-recent-login') {
        set({
          error: 'Please sign out and sign in again, then try deleting your account.',
          loading: false
        });
      } else {
        const message = getAuthErrorMessage(error);
        set({ error: message, loading: false });
      }
      throw error;
    }
  },

  /**
   * Fetch user document (fallback for when real-time listener fails)
   */
  fetchUserDoc: async () => {
    const { user } = get();
    if (!user) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(userDocRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const normalizedDoc = normalizeUserDoc(data);

        // Check if de-escalation mode has expired
        if (normalizedDoc.mode === 'deescalation' && normalizedDoc.modeUntil) {
          if (normalizedDoc.modeUntil.getTime() < Date.now()) {
            normalizedDoc.mode = 'normal';
            normalizedDoc.modeUntil = undefined;
          }
        }

        set({ userDoc: normalizedDoc });
      }
    } catch (error) {
      // Network errors will auto-retry when online via the listener
      // Other errors are non-fatal - user can still use app with limited data
      if (!isNetworkError(error)) {
        // Set error to show user, but don't throw
        set({ error: 'Could not load your profile. Some features may be limited.' });
      }
    }
  },

  /**
   * Update user settings with optimistic update
   * If userDoc doesn't exist yet (new user after onboarding), creates the full document
   */
  updateUserSettings: async (updates) => {
    const { user, userDoc } = get();
    if (!user) return;

    const userDocRef = doc(db, 'users', user.uid);

    // If no userDoc exists yet (new user completing onboarding), create full document
    if (!userDoc) {
      const newUserDoc: UserDocument = {
        displayName: (updates as Partial<UserDocument>).displayName || user.displayName || user.phoneNumber || 'Parent',
        createdAt: new Date(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        defaultTimeOption: ((updates as Partial<UserDocument>).defaultTimeOption || 3) as TimeOption,
        focusAreas: (updates as Partial<UserDocument>).focusAreas || [],
        mode: 'normal',
        notificationPrefs: { enabled: false },
        momentum7d: 0,
        totalCredits: 0,
        onboardingCompleted: true,
        ...updates,
      };

      // Set in local state immediately
      set({ userDoc: newUserDoc });

      try {
        await setDoc(userDocRef, {
          ...newUserDoc,
          createdAt: Timestamp.now(),
        });
        // Real-time listener will update the state
      } catch (error) {
        set({ userDoc: null, error: 'Failed to create profile. Please try again.' });
        throw error;
      }
      return;
    }

    // Existing user - optimistic update
    const previousDoc = userDoc;
    set({ userDoc: { ...userDoc, ...updates } });

    try {
      await setDoc(userDocRef, updates, { merge: true });
      // Real-time listener will update the state
    } catch (error) {
      // Rollback on error
      set({ userDoc: previousDoc, error: 'Failed to save settings. Please try again.' });
      throw error;
    }
  },

  /**
   * Force refresh the ID token
   * Useful before making sensitive operations
   */
  refreshToken: async () => {
    const { user } = get();
    if (!user) return null;

    try {
      const token = await user.getIdToken(true);
      return token;
    } catch (error) {
      // If token refresh fails, user may need to re-authenticate
      if ((error as { code?: string }).code === 'auth/user-token-expired') {
        set({ error: 'Your session has expired. Please sign in again.' });
        await get().signOut();
      }
      return null;
    }
  },

  /**
   * Clear error state
   */
  clearError: () => set({ error: null }),
}));

